import { FC, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { otpAsync } from "redux/actions/Otp.action";
import {
  resetOtp,
  selectSuccess,
  resetOtpErrorAlert,
  selectIsIgsLocked,
} from "redux/slices/OtpSlice";
import { forwardStep, selectStep, resetStep } from "redux/slices/StepSlice";
import { selectUser, ResetLogin } from "redux/slices/LoginSlice";

import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { ILoginRequest } from "models/Login/login.model";
import { loginAsync } from "redux/actions/Login.action";

import {
  Button,
  OtpTextField,
  Typography,
  Panel,
  Container,
  PasswordBlockModal,
} from "components";
import Image, { ICONS } from "components/BaseComponents/Image";

import { Col, ElementWrapper, Row } from "components/BaseComponents/Flex";

import { DIGIPOSTHEME } from "theme/digiPosTheme";
import { useMediaQuery } from "context/media/MediaContext";

import {
  OTP_RELOGIN,
  OTP_TIME,
  OTP_TIME_MINUTES,
  OTP_TIME_SECONDS,
} from "./Otp.constants";
import { StyledOuterContainer, StyledRepeatButton } from "./Otp.styles";
import { IOtpRequest } from "models/Otp/otp.model";
import PanelHeader from "components/BaseComponents/Panel/Panel.Header";
import { otpValidationSchema } from "validations/otp.validation";
import {
  IPasswordCardInfoRequest,
  IPasswordPersonelInfo,
  INewPasswordPersonelInfoRequest,
} from "models/Password/password.model";
import {
  PasswordRefreshCardInfoAsync,
  PasswordRefreshPersonelInfoAsync,
} from "redux/actions/password/Password.action";
import Alert from "components/BaseComponents/Alert/Alert";

type otpProps = {
  fromGetPassword?: boolean;
};
const Otp: FC<otpProps> = ({ fromGetPassword }) => {
  const navigate = useNavigate();
  const { isMobile } = useMediaQuery();
  const { t } = useTranslation();
  const location = useLocation();
  let fromLogin: boolean = location?.state?.fromLogin || false;

  const dispatch = useAppDispatch();
  const selectedStep = useAppSelector(selectStep);
  const success = useAppSelector(selectSuccess);
  const IgsLocked = useAppSelector(selectIsIgsLocked);
  const loginUser = useAppSelector(selectUser);
  const { errorAlert, userOtp } = useAppSelector((state) => state.otp);
  const { cardInfo } = useAppSelector((state) => state.PassRefCardInfo);
  const { passwordInfo } = useAppSelector((state) => state.PassRefPersonelInfo);

  const [timer, setTimer] = useState<number>(OTP_TIME);
  const [minutes, setMinutes] = useState<number>(OTP_TIME_MINUTES);
  const [seconds, setSeconds] = useState<number>(OTP_TIME_SECONDS);
  const [timeDiff, setTimeDiff] = useState<string>("");

  const [isAddError, setIsAddError] = useState<boolean>(false);
  const [isAddSuccess, setIsAddSuccess] = useState<boolean>(false);
  const [errMessage, setErrMessage] = useState<string>("");

  const [isPassBlockModalOpen, setIsPassBlockModalOpen] = useState<boolean>(
    false
  );

  const { control, handleSubmit, setValue, errors, reset } = useForm({
    defaultValues: { otpRefNo: "" },
    resolver: yupResolver(otpValidationSchema(t)),
  });

  const handleRepeatClick = async () => {
    if (fromGetPassword) {
      // const reCardInfo: IPasswordCardInfoRequest = {
      //   PinDigitEnc: cardInfo?.cardTemp?.PinDigitEnc,
      //   TokenSessionId: cardInfo?.cardTemp?.TokenSessionId,
      // };
      // dispatch(PasswordRefreshCardInfoAsync(reCardInfo));
      const rePersonelInfo: INewPasswordPersonelInfoRequest = {
        phoneNumber: passwordInfo?.personelInfoTemp?.phoneNumber!,
        merchantNo: passwordInfo?.personelInfoTemp?.merchantNo!,
        IsFirst: false,
      };
      dispatch(PasswordRefreshPersonelInfoAsync(rePersonelInfo));
    } else {
      const reLoginUser: ILoginRequest = {
        TokenSessionId: loginUser?.userTemp?.TokenSessionId,
        password: loginUser?.userTemp?.igsEnc,
        phoneNumber: loginUser?.userTemp?.mbb,
        userId: loginUser?.userTemp?.UserId,
        corporateIndividual: loginUser?.userTemp?.corporateIndividual,
        reLogin: OTP_RELOGIN,
      };
      dispatch(loginAsync(reLoginUser));
    }
    setTimer(OTP_TIME);
    setSeconds(OTP_TIME_SECONDS);
    setMinutes(OTP_TIME_MINUTES);
    reset();
  };

  const user: IOtpRequest = {
    maskedPhoneNumber: loginUser?.sifreTel || cardInfo?.DefaultPhone,
    SmsOtpId: loginUser?.otpId || loginUser?.OtpId || cardInfo?.SmsOtpId,
  };
  const onSubmit = (data: IOtpRequest) => {
    data.maskedPhoneNumber = user.maskedPhoneNumber;
    data.SmsOtpId = user.SmsOtpId;
    data.fromGetPassword = fromGetPassword;
    dispatch(otpAsync(data));
  };

  useEffect(() => {
    if (success && !IgsLocked) {
      if (!fromLogin && selectedStep === 1 && fromGetPassword) {
        dispatch(forwardStep(2));
      } else {
        navigate("/");
      }
      // if (!fromLogin && selectedStep === 2 && fromGetPassword) {
      //   dispatch(forwardStep(3));
      // } else {
      //   navigate("/");
      // }
    } else if (IgsLocked) {
      setIsPassBlockModalOpen(true);
    }

    setValue("otpRefNo", "");
  }, [userOtp, navigate]);

  useEffect(() => {
    if (errorAlert) {
      setIsAddError(true);
      setErrMessage(errorAlert);
      dispatch(resetOtpErrorAlert());
      reset();
    }
  }, [errorAlert]);

  const OtpPageExit = () => {
    dispatch(resetStep());
    dispatch(ResetLogin());
    dispatch(resetOtp());
    dispatch(forwardStep(1));
    navigate(-1);
  };

  const securityImage = loginUser?.securityImage || loginUser?.SecurityImage;

  return (
    <Container>
      <ElementWrapper
        minHeight={isMobile ? "calc(100vh - 118px)" : "calc(100vh - 137px)"}
        width='872px'
        justifyContent={isMobile ? "flex-start" : "center"}
      >
        <StyledOuterContainer
          paddingTop={!fromLogin && selectedStep === 2 ? "0px" : "20px"}
        >
          {isAddError && (
            <Alert
              alertType={"Error"}
              duration={6000}
              isVisible={isAddError}
              message={errMessage}
              setIsAddSuccess={setIsAddSuccess}
              setIsAddError={setIsAddError}
            />
          )}
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Panel>
              {selectedStep === 2 && (
                <PanelHeader title={t("change_password_otp_verification")} />
              )}
              <Typography
                letterSpacing='0.7px'
                padding='10px'
                size='13px'
                variant='p'
                weight='500'
                width='auto'
                color={DIGIPOSTHEME.colors.secondaryWhite}
                backgroundColor={DIGIPOSTHEME.colors.red}
                borderRadius='4px'
                display={isMobile ? "flex" : "contents"}
              >
                <Trans
                  i18nKey='otp_info_message'
                  values={{
                    maskedPhoneNumber: user?.maskedPhoneNumber,
                    otpId: user.SmsOtpId,
                  }}
                />
              </Typography>
              {securityImage && (
                <Panel marginTop='20px' padding={isMobile ? "0" : "auto"}>
                  <PanelHeader title={t("otp_submit_info_message")} />
                  <Row noMargin>
                    <Col maxWidth='100px'>
                      <Image
                        alt='Security Image'
                        height='80px'
                        srcSet={
                          securityImage
                            ? `data:image/png;base64, ${securityImage}`
                            : undefined
                        }
                        width='90px'
                        borderRadius='5px'
                        border='1px solid #ccc'
                        margin='10px 0 10px 10px'
                        padding='5px'
                      />
                    </Col>
                    <Col>
                      <Typography
                        letterSpacing='0.7px'
                        padding='10px'
                        size='13px'
                        variant='p'
                        weight='500'
                        width='auto'
                      >
                        <Trans
                          i18nKey='otp_submit_security_image_info_message'
                          values={{ phoneNumber: " 444 25 25" }}
                        />
                      </Typography>
                    </Col>
                  </Row>
                </Panel>
              )}
              <OtpTextField
                control={control}
                error={errors.otpRefNo?.message}
                label={t("phone_password").toString()}
                name='otpRefNo'
                timer={timer}
                setTimer={setTimer}
                minutes={minutes}
                setMinutes={setMinutes}
                setSeconds={setSeconds}
                seconds={seconds}
                setTimeDiff={setTimeDiff}
                timeDiff={timeDiff}
              />

              {timer === 0 && (
                <StyledRepeatButton onClick={handleRepeatClick}>
                  <Image
                    alt='Repeat Icon'
                    height='32px'
                    srcSet={ICONS.REPEAT}
                    width='32px'
                  />

                  <Trans i18nKey='resend' />
                </StyledRepeatButton>
              )}
            </Panel>
            <Row
              backgroundColor={DIGIPOSTHEME.colors.secondaryOffWhite}
              justifyContent='flex-end'
              padding={isMobile ? "32px 20px 10px 20px" : "32px 0 0 0"}
              isResponsive
              noMargin
              colReverse
            >
              <Col>
                <Button
                  type='button'
                  width={isMobile ? "100%" : "100px"}
                  text={`← ${t("back")}`}
                  onClick={() => OtpPageExit()}
                  color='light'
                />
              </Col>
              <Col offSet={22} size={0.5}>
                <Button
                  disabled={timer === 0}
                  text={t("continue").toString()}
                  type='submit'
                />
              </Col>
            </Row>
          </form>
        </StyledOuterContainer>
      </ElementWrapper>
      {isPassBlockModalOpen && (
        <PasswordBlockModal
          acceptButtonText={t("modal_error_button_title").toString()}
          message={t("user_admin_agent_lock_password").toString()}
          setIsOpen={setIsPassBlockModalOpen}
          title={t("information").toString()}
        />
      )}
    </Container>
  );
};

export default Otp;
